// utils/dropdownPositioning.ts
export interface PositionInfo {
  top: number;
  left: number;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'right-top' | 'right-bottom' | 'left-top' | 'left-bottom';
  debug?: {
    availableSpace: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
    elementSize: {
      width: number;
      height: number;
    };
    triggerRect: DOMRect;
    parentRect?: DOMRect;
  };
}

export const calculateSmartPosition = (
  triggerElement: HTMLElement,
  contentWidth: number,
  contentHeight: number,
  options: {
    preferredPosition?: 'bottom' | 'top' | 'right' | 'left';
    parentElement?: HTMLElement | null;
    offset?: number;
    debug?: boolean;
  } = {}
): PositionInfo => {
  const { 
    preferredPosition = 'bottom', 
    parentElement = null, 
    offset = 8,
    debug = false 
  } = options;

  const triggerRect = triggerElement.getBoundingClientRect();
  const parentRect = parentElement?.getBoundingClientRect();
  
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollX = window.scrollX || window.pageXOffset;
  const scrollY = window.scrollY || window.pageYOffset;

  // Calculate available space in all directions
  const availableSpace = {
    top: triggerRect.top,
    right: viewportWidth - triggerRect.right,
    bottom: viewportHeight - triggerRect.bottom,
    left: triggerRect.left,
  };

  // If parent element exists, also consider space relative to parent
  if (parentRect) {
    availableSpace.right = Math.min(availableSpace.right, viewportWidth - parentRect.right);
    availableSpace.left = Math.min(availableSpace.left, parentRect.left);
  }

  // Priority order for positions based on preferred position
  const positionPriority: Record<string, PositionInfo['position'][]> = {
    bottom: ['bottom-right', 'bottom-left', 'top-right', 'top-left', 'right-top', 'left-top'],
    top: ['top-right', 'top-left', 'bottom-right', 'bottom-left', 'right-bottom', 'left-bottom'],
    right: ['right-top', 'right-bottom', 'left-top', 'left-bottom', 'bottom-right', 'top-right'],
    left: ['left-top', 'left-bottom', 'right-top', 'right-bottom', 'bottom-left', 'top-left'],
  };

  const positions = positionPriority[preferredPosition] || positionPriority.bottom;

  // Check each position for fit
  for (const position of positions) {
    let fits = false;
    let top = 0;
    let left = 0;

    switch (position) {
      case 'bottom-right':
        fits = availableSpace.bottom >= contentHeight + offset && availableSpace.right >= contentWidth - triggerRect.width;
        top = triggerRect.bottom + scrollY + offset;
        left = triggerRect.left + scrollX;
        break;

      case 'bottom-left':
        fits = availableSpace.bottom >= contentHeight + offset && availableSpace.left >= contentWidth - triggerRect.width;
        top = triggerRect.bottom + scrollY + offset;
        left = triggerRect.right + scrollX - contentWidth;
        break;

      case 'top-right':
        fits = availableSpace.top >= contentHeight + offset && availableSpace.right >= contentWidth - triggerRect.width;
        top = triggerRect.top + scrollY - contentHeight - offset;
        left = triggerRect.left + scrollX;
        break;

      case 'top-left':
        fits = availableSpace.top >= contentHeight + offset && availableSpace.left >= contentWidth - triggerRect.width;
        top = triggerRect.top + scrollY - contentHeight - offset;
        left = triggerRect.right + scrollX - contentWidth;
        break;

      case 'right-top':
        fits = availableSpace.right >= contentWidth + offset && availableSpace.bottom >= contentHeight - triggerRect.height;
        top = triggerRect.top + scrollY;
        left = triggerRect.right + scrollX + offset;
        break;

      case 'right-bottom':
        fits = availableSpace.right >= contentWidth + offset && availableSpace.top >= contentHeight - triggerRect.height;
        top = triggerRect.bottom + scrollY - contentHeight;
        left = triggerRect.right + scrollX + offset;
        break;

      case 'left-top':
        fits = availableSpace.left >= contentWidth + offset && availableSpace.bottom >= contentHeight - triggerRect.height;
        top = triggerRect.top + scrollY;
        left = triggerRect.left + scrollX - contentWidth - offset;
        break;

      case 'left-bottom':
        fits = availableSpace.left >= contentWidth + offset && availableSpace.top >= contentHeight - triggerRect.height;
        top = triggerRect.bottom + scrollY - contentHeight;
        left = triggerRect.left + scrollX - contentWidth - offset;
        break;
    }

    if (fits) {
      // Ensure position doesn't go off-screen
      left = Math.max(offset, Math.min(left, viewportWidth - contentWidth - offset));
      top = Math.max(offset, Math.min(top, viewportHeight - contentHeight - offset + scrollY));

      const result: PositionInfo = { top, left, position };
      
      if (debug) {
        result.debug = {
          availableSpace,
          elementSize: { width: contentWidth, height: contentHeight },
          triggerRect,
          parentRect,
        };
      }

      return result;
    }
  }

  // Fallback: center on screen if no position fits
  const fallbackPosition: PositionInfo = {
    top: Math.max(offset, (viewportHeight - contentHeight) / 2 + scrollY),
    left: Math.max(offset, (viewportWidth - contentWidth) / 2 + scrollX),
    position: 'bottom-right',
  };

  if (debug) {
    fallbackPosition.debug = {
      availableSpace,
      elementSize: { width: contentWidth, height: contentHeight },
      triggerRect,
      parentRect,
    };
  }

  return fallbackPosition;
};